import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Wrench, Sparkles, Zap, Trees, Lock, Building2 } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import apiService from '@/services/api';

const LandlordsPage = () => {
  const [sections, setSections] = useState<any>({});

  useEffect(() => {
    const fetchPageSections = async () => {
      try {
        const response = await apiService.get('/public/page-sections/landlords');
        if (response.success && Array.isArray(response.data)) {
          const sectionMap: Record<string, any> = {};
          response.data.forEach((section: any) => {
            try {
              sectionMap[section.section_key] = {
                ...section,
                body: section.body ? JSON.parse(section.body) : section.body
              };
            } catch (e) {
              sectionMap[section.section_key] = section;
            }
          });
          setSections(sectionMap);
        }
      } catch (error) {
        console.error('Error fetching page sections:', error);
      }
    };
    fetchPageSections();
  }, []);
  const services = [
    {
      icon: Wrench,
      title: "Master Plumbing",
      description: "Emergency response and preventative maintenance. Certified plumbers ensure your portfolio's water systems run flawlessly."
    },
    {
      icon: Sparkles,
      title: "Elite Housekeeping",
      description: "Professional turnover cleaning and ongoing maintenance. Meticulous standards between tenants and routine duties."
    },
    {
      icon: Zap,
      title: "Precision Electrical",
      description: "Safe, code-compliant electrical work across your portfolio. Certified technicians with compliance documentation."
    },
    {
      icon: Trees,
      title: "Landscape Curation",
      description: "Seasonal landscaping and garden maintenance that preserves property values and tenant satisfaction."
    },
    {
      icon: Lock,
      title: "Security & Access",
      description: "Smart lock systems and security solutions integrated across multiple properties with centralized oversight."
    },
    {
      icon: Building2,
      title: "Project Management",
      description: "Multi-property coordination with unified scheduling, budgeting, and stakeholder communication."
    },
  ];

  const testimonials = [
    {
      quote: "Managing 12 properties used to be chaotic. Now I have one platform that handles coordination, compliance, and tenant experience. Virewire has become indispensable.",
      author: "James Henderson",
      role: "Portfolio Manager, Mayfair Properties"
    },
    {
      quote: "The level of professionalism and organization is unmatched. Between tenant turnovers and maintenance requests, Virewire keeps everything running smoothly across our entire portfolio.",
      author: "Sarah Mitchell",
      role: "Letting Agent, Central London"
    }
  ];

  return (
    <Layout>
      <Helmet>
        <title>For Landlords & Agents | Virewire Managed Services</title>
        <meta name="description" content="Professional property services for landlords and letting agents. Portfolio coordination, turnovers, maintenance. B2B pricing, compliance documentation, dedicated support." />
        <link rel="canonical" href="https://virewire.co.uk/for/landlords" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-teal-900 via-teal-800 to-teal-700 text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-teal-200 text-sm font-semibold uppercase tracking-widest mb-4">
                For Landlords & Agents
              </p>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Your Portfolio,<br />Professionally <span className="italic">Managed.</span><br />No Contractor<br />Hassle.
              </h1>
              <p className="text-xl text-teal-100 mb-8 leading-relaxed">
                Virewire orchestrates all property services across your entire portfolio. From tenant turnovers to emergency repairs, we handle coordination, compliance, and quality oversight.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button className="bg-white text-teal-700 hover:bg-gray-100 px-8 py-3 text-lg font-semibold" asChild>
                  <Link to="/request-service">Schedule a Demo</Link>
                </Button>
                <Button variant="outline" className="border-white text-white hover:bg-teal-600 px-8 py-3 text-lg font-semibold" asChild>
                  <Link to="/contact">Speak with Our Team</Link>
                </Button>
              </div>
            </div>
            <div className="bg-gray-900 rounded-lg h-96 flex items-center justify-center">
              <div className="text-gray-400 text-center">
                <div className="text-8xl mb-2">🏢</div>
                <p>Portfolio Management</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-3xl md:text-4xl font-bold text-teal-700">500+</p>
              <p className="text-sm text-gray-600 uppercase tracking-wide mt-2">Landlords & Agents</p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl font-bold text-teal-700">2,500+</p>
              <p className="text-sm text-gray-600 uppercase tracking-wide mt-2">Properties Managed</p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl font-bold text-teal-700">99%</p>
              <p className="text-sm text-gray-600 uppercase tracking-wide mt-2">On-Time Delivery</p>
            </div>
            <div>
              <p className="text-3xl md:text-4xl font-bold text-teal-700">24/7</p>
              <p className="text-sm text-gray-600 uppercase tracking-wide mt-2">Account Support</p>
            </div>
          </div>
        </div>
      </section>

      {/* Comprehensive Portfolio Management */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-16">
            <p className="text-teal-600 font-semibold uppercase tracking-wide text-sm mb-2">Our Expertise</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Comprehensive Portfolio<br />Care for the Professional<br />Manager.
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl">
              Single coordination point for every property service. Managed with institutional standards.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service) => {
              const Icon = service.icon;
              return (
                <div key={service.title} className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-lg transition">
                  <div className="bg-teal-50 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="text-teal-700" size={24} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  <Link to="/services" className="text-teal-600 font-semibold text-sm mt-4 inline-block hover:text-teal-700">
                    EXPLORE SERVICE →
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Three Steps to Sovereignty */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-teal-600 font-semibold uppercase tracking-wide text-sm mb-2">The Process</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              Three Steps to Portfolio Sovereignty
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <p className="text-6xl font-bold text-gray-300 mb-4">01</p>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Portfolio Assessment</h3>
              <p className="text-gray-600">
                Complete audit of your properties, tenants, and current service challenges to build a tailored coordination plan.
              </p>
            </div>
            <div className="text-center">
              <p className="text-6xl font-bold text-gray-300 mb-4">02</p>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Centralized Roadmap</h3>
              <p className="text-gray-600">
                Creation of unified service roadmap across your entire portfolio with consolidated scheduling and budgeting.
              </p>
            </div>
            <div className="text-center">
              <p className="text-6xl font-bold text-gray-300 mb-4">03</p>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Managed Operations</h3>
              <p className="text-gray-600">
                Ongoing platform coordination with dedicated account manager, compliance documentation, and performance reporting.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="bg-gray-300 rounded-lg h-96 flex items-center justify-center">
              <div className="text-gray-500 text-center">
                <div className="text-6xl mb-2">🏘️</div>
                <p>Property Portfolio</p>
              </div>
            </div>
            <div className="space-y-8">
              <p className="text-sm font-semibold uppercase tracking-wide text-teal-600">Client Stories</p>
              {testimonials.map((testimonial, idx) => (
                <div key={idx}>
                  <p className="text-xl md:text-2xl font-serif text-gray-900 mb-4 leading-relaxed">
                    "{testimonial.quote}"
                  </p>
                  <p className="font-semibold text-gray-900">{testimonial.author}</p>
                  <p className="text-sm text-gray-600">{testimonial.role}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-teal-700 text-white rounded-3xl mx-4 md:mx-8 lg:mx-16 mb-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <svg className="w-full h-full" viewBox="0 0 1200 600" preserveAspectRatio="none">
            <circle cx="1200" cy="300" r="400" fill="white" opacity="0.1" />
            <circle cx="1200" cy="300" r="300" fill="white" opacity="0.1" />
          </svg>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center px-8">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Ready to Restore<br />Order to Your Portfolio?
          </h2>
          <p className="text-lg text-teal-50 mb-8">
            Join 500+ landlords and agents who have delegated property coordination to the professionals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-white text-teal-700 hover:bg-gray-100 px-8 py-3 text-lg font-semibold" asChild>
              <Link to="/request-service">Request a Service</Link>
            </Button>
            <Button variant="outline" className="border-white text-white hover:bg-teal-600 px-8 py-3 text-lg font-semibold" asChild>
              <Link to="/contact">Speak with Our Team</Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default LandlordsPage;
